import React from 'react';
import { styled } from '@mui/material/styles';
import { radius } from '../theme/tokens.radius';
import { UIKitButtonProps } from './Button';

interface UIKitButtonGroupProps {
  children: React.ReactNode; // se espera solo UIKitButton como hijos
  fullWidth?: boolean;
  className?: string;
}

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  ...(fullWidth && { width: '100%' }),
}));

// item's styles (display:flex, alignItems:stretch) are applied to arbitrary
// cloned children below via inline `style`, not a className - a styled()
// wrapper would add an extra DOM node per child and break the flex-stretch
// layout (children need to be direct flex items of Wrapper).
const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
};

const UIKitButtonGroup: React.FC<UIKitButtonGroupProps> = ({
  children,
  fullWidth = false,
  className,
}) => {
  const childArray = React.Children.toArray(children) as React.ReactElement<UIKitButtonProps>[];

  return (
    <Wrapper fullWidth={fullWidth} className={className}>
      {childArray.map((child, index) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        const isFirst = index === 0;
        const isLast = index === childArray.length - 1;

        const stylePatch: React.CSSProperties = {};

        if (childArray.length === 1) {
          // solo un botón → píldora completa
          stylePatch.borderRadius = radius.full;
        } else if (isFirst) {
          // primer botón → radios izquierdos
          stylePatch.borderRadius = `${radius.full}px 0 0 ${radius.full}px`;
        } else if (isLast) {
          // último botón → radios derechos
          stylePatch.borderRadius = `0 ${radius.full}px ${radius.full}px 0`;
        } else {
          // botones del medio → sin radios
          stylePatch.borderRadius = 0;
        }

        return React.cloneElement<UIKitButtonProps>(child, {
          className: child.props.className,
          style: {
            ...itemStyle,
            ...child.props.style,
            ...stylePatch, // el grupo manda sobre el borderRadius
          },
        });
      })}
    </Wrapper>
  );
};

export default UIKitButtonGroup;
